import {
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useQueryParams, StringParam } from "use-query-params";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";

import {
  IBandSubscription,
  IBandSubscriptionParams,
  ICompany,
  ISubscription,
  SubscriptionType,
  IBandSubscriptionRequest,
  IEditBandSubscriptionParams,
  ICustomSubscriptionRequestParams,
} from "Bands/types";
import { SUBSCRIPTIONS } from "Bands/constants";
import { useBandContext, useCompanyContext } from "Bands/contexts";
import { API_ENDPOINTS } from "shared/constants";
import { useBandRoute, useCompanyRoute } from "Bands/utils";
import { BAND_PATHS } from "Bands/router";
import {
  createBandSubscription,
  editBandSubscription,
  requestCustomSubscription,
} from "Bands/state";

interface SubscriptionContextState {
  selectedSubscription: ISubscription | null;
  selectedBilling: ICompany | null;
  subscriptions: Record<string, ISubscription>;
  chooseSubscription: (type: SubscriptionType) => void;
  chooseBillingMethod: (companyId: string) => void;
  editSubscription: (props: {
    plan: SubscriptionType;
    currentSubscription: IBandSubscription;
  }) => void;
  handleSubscribe: (
    newBilling?: ICompany,
    newSubscription?: ISubscription
  ) => void;
  redirectToStripe: (props: {
    subscriptionRequest?: IBandSubscriptionRequest;
    companyId: string;
  }) => void;
}

const initialState: SubscriptionContextState = {
  selectedSubscription: null,
  selectedBilling: null,
  subscriptions: {},
  chooseSubscription: () => {},
  chooseBillingMethod: () => {},
  editSubscription: () => {},
  handleSubscribe: () => {},
  redirectToStripe: () => {},
};

const SubscriptionContext = createContext(initialState);

export const SubscriptionProvider: FC = ({ children }) => {
  const [query] = useQueryParams({
    plan: StringParam,
    company: StringParam,
  });

  const [selectedSubscription, setSelectedSubscription] =
    useState<ISubscription | null>(null);
  const [selectedBilling, setSelectedBilling] = useState<ICompany | null>(null);
  const { userCompanies } = useCompanyContext();
  const { bandId } = useBandContext();
  const { getPath: getCompanyPath } = useCompanyRoute();
  const { getPath: getBandPath } = useBandRoute();

  const dispatch = useDispatch();
  const history = useHistory();

  const chooseSubscription = useCallback((type: SubscriptionType) => {
    const subscription = SUBSCRIPTIONS[type];
    subscription && setSelectedSubscription(subscription);
  }, []);

  const chooseBillingMethod = useCallback(
    (companyId: string) => {
      const company = userCompanies.find(({ id }) => id === companyId);
      company && setSelectedBilling(company);
    },
    [userCompanies]
  );

  const handleSubscribe = useCallback(
    (newBilling, newSubscription) => {
      const currentBilling = newBilling || selectedBilling;
      const currentSubscription = newSubscription || selectedSubscription;

      if (!currentBilling) return;
      if (!currentSubscription) return;
      if (!bandId) return;

      const company = API_ENDPOINTS.COMPANY(currentBilling.id);
      const plan = currentSubscription.plan;

      // different call for custom subscription
      if (plan === SubscriptionType.custom) {
        const customSubscription: ICustomSubscriptionRequestParams = {
          bands: [API_ENDPOINTS.BAND(bandId)],
          company,
        };

        dispatch(requestCustomSubscription(customSubscription));
        return;
      }

      // not custom subscription
      const subscription: IBandSubscriptionParams = {
        bands: [API_ENDPOINTS.BAND(bandId)],
        company,
        plan,
        successUrl: getBandPath(
          `${window.location.origin}${BAND_PATHS.SUBSCRIPTION_SUCCESS}?plan=${plan}&company=${currentBilling.id}`
        ),
        cancelUrl: getBandPath(
          `${window.location.origin}${BAND_PATHS.SUBSCRIPTION_ERROR}?action=create`
        ),
      };

      dispatch(createBandSubscription(subscription));
    },
    [dispatch, bandId, selectedBilling, selectedSubscription, getBandPath]
  );

  const editSubscription = useCallback(
    ({
      plan,
      currentSubscription,
    }: {
      plan: SubscriptionType;
      currentSubscription: IBandSubscription;
    }) => {
      if (bandId) {
        const data: IEditBandSubscriptionParams = {
          subscriptionId: currentSubscription.id,
          plan,
        };

        dispatch(editBandSubscription(data));
      }
    },
    [bandId, dispatch]
  );

  const redirectToStripe = useCallback(
    ({
      subscriptionRequest,
      companyId,
    }: {
      subscriptionRequest?: IBandSubscriptionRequest;
      companyId: string;
    }) => {
      const { paymentUrl } = subscriptionRequest || {};

      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else {
        const companyDashboard = getCompanyPath(BAND_PATHS.COMPANY_DASHBOARD, {
          companyId,
        });

        history.push(companyDashboard);
      }
    },
    [getCompanyPath, history]
  );

  useEffect(() => {
    if (query.company) {
      chooseBillingMethod(query.company);
    }

    if (query.plan) {
      chooseSubscription(query.plan as SubscriptionType);
    }
  }, [query, chooseBillingMethod, chooseSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        selectedSubscription,
        selectedBilling,
        subscriptions: SUBSCRIPTIONS,
        chooseSubscription,
        chooseBillingMethod,
        editSubscription,
        handleSubscribe,
        redirectToStripe,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionContext = () => useContext(SubscriptionContext);
